/**
 * Which slices of the attendance archive a teacher is allowed to ask about.
 *
 * This is a *menu* builder, not a permission check. The permission lives in one place — the
 * `staff_attendance_history` RPC repeats the RLS policy's own test, so a hand-typed URL
 * asking for Camps or for `chinese:4` gets nothing back if the caller's grant does not cover
 * it. What this decides is which tabs are worth drawing, so a Chinese-only teacher is not
 * offered four empty bands and a grade she does not teach.
 *
 * The two halves of a teacher's grant work differently, and the difference is the whole of
 * the logic here:
 *
 *   `program_ids`         `null` means EVERY program; a list means those; `[]` means none.
 *   `virtual_class_keys`  `null` and `[]` both mean none. A virtual class is only ever an
 *                         explicit grant (see the column comment in the DB).
 */

import {
  HISTORY_BAND_ORDER,
  type HistoryBand,
} from '../hooks/useAttendanceHistory';
import { NON_TEACHING_PROGRAM_TYPES } from './programSessions';
import { resolveVirtualClasses, type VirtualGrade } from './virtualClasses';

/** The two bands that are virtual classes rather than programs. */
export type SubjectBand = 'chinese' | 'math';

export function isSubjectBand(band: HistoryBand): band is SubjectBand {
  return band === 'chinese' || band === 'math';
}

export interface TeacherHistoryScope {
  /** The bands worth offering, in `HISTORY_BAND_ORDER`. Empty for a teacher with no grant. */
  bands: HistoryBand[];
  /** The grades this teacher holds in each subject, in roster order. */
  gradesBySubject: Record<SubjectBand, VirtualGrade[]>;
}

export const EMPTY_HISTORY_SCOPE: TeacherHistoryScope = {
  bands: [],
  gradesBySubject: { chinese: [], math: [] },
};

/**
 * The bands and grades to offer, from the teacher's grant.
 *
 * `programTypes` is the distinct `programs.type` values behind their `program_ids` — every
 * type in the catalog when that column is null. Types are used rather than the ids
 * themselves because a band *is* a set of types: the archive's five bands partition rows by
 * `programs.type` plus the virtual-class discriminator, exactly as the admin archive does.
 *
 * Non-teaching types are dropped before the Enrichment test. A `registration` product
 * carries enrollments and can carry a stray attendance row, but the teacher portal hides
 * those programs everywhere else (see `NON_TEACHING_PROGRAM_TYPES`), and a band that exists
 * only to show them would be a menu entry no teacher has a question for. The RPC still
 * counts them under Enrichment, so the two agree about what a row *is* — they differ only
 * about whether the tab is worth drawing.
 */
export function buildTeacherHistoryScope(
  programTypes: readonly string[],
  virtualClassKeys: readonly string[] | null | undefined,
): TeacherHistoryScope {
  const types = new Set(
    programTypes.filter(type => !NON_TEACHING_PROGRAM_TYPES.includes(type)),
  );

  const held = resolveVirtualClasses(virtualClassKeys);
  const gradesBySubject: Record<SubjectBand, VirtualGrade[]> = {
    chinese: held.filter(vc => vc.subject.id === 'chinese').map(vc => vc.grade),
    math: held.filter(vc => vc.subject.id === 'math').map(vc => vc.grade),
  };

  const has: Record<HistoryBand, boolean> = {
    afterschool: types.has('after-school'),
    camp: types.has('camp'),
    // Everything-else, the same fallback `pillarOfProgramType` uses: a program type nobody
    // has thought of yet gets a tab the day it is created, without anyone coming here.
    enrichment: [...types].some(type => type !== 'after-school' && type !== 'camp'),
    chinese: gradesBySubject.chinese.length > 0,
    math: gradesBySubject.math.length > 0,
  };

  return {
    bands: HISTORY_BAND_ORDER.filter(band => has[band]),
    gradesBySubject,
  };
}

/**
 * Where the archive opens for this teacher.
 *
 * Their first band, which is `HISTORY_BAND_ORDER`'s first — After School when they run it,
 * otherwise whatever they do hold. A Chinese teacher lands on Chinese rather than on an
 * After School tab that would show them a hundred handovers they had no part in.
 */
export function defaultHistoryBand(scope: TeacherHistoryScope): HistoryBand | null {
  return scope.bands[0] ?? null;
}
